import { IndustryTag } from '@assets/component/IndustryTagSelect/IndustryTagSelect';
import { useScoreStore } from '@assets/model/ScoreUtils';
import { useConfig } from '@assets/model/StoreConfig';
import { useBacktestingStore } from '@assets/model/StoreBacktesting';
import * as Sentry from '@sentry/react';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import {
    Database,
    getDatabase,
    onValue,
    push,
    ref,
    serverTimestamp,
    set,
    update,
    get,
    remove,
    query,
    orderByKey,
    limitToLast
} from 'firebase/database';
import {
    useClientSettingStore,
    useStore,
    useStoreUI,
    useTasksStore
} from 'src/model/Store';
import { DataAccessUtils } from 'src/model/DataAccessUtils';

interface SavedComparable {
    dealId: string | null;
    position: number;
}

type AuditEntryType =
    | 'LOGIN'
    | 'APP_LOAD'
    | 'VIEW_PAGE_DEAL'
    | 'VIEW_PAGE_DB'
    | 'EDIT_FIELD'
    | 'ADD_NEW_DEAL'
    | 'DOWNLOAD_CIM'
    | 'UPLOAD_CIM'
    | 'DELETE_DEAL'
    | 'SAVE_REFERENCE_DATA';

export type FlowTaskMessage = {
    content: string;
    approve?: boolean;
    attachedFiles?: string[];
    // Per-file content fingerprints used by the comparator detector. Each
    // entry is the ragDocId (UUID v5 derived from file content) when the RAG
    // pipeline has processed the file, or the bare filename as fallback.
    // Same content → same fingerprint regardless of name; different content →
    // different fingerprint even with same filename.
    attachedFilesFingerprints?: string[];
};

// Marker the user prepends (invisibly) when sending a refinement message via
// the staging proposal bubble's Discuss flow. Master JS recognizes the marker,
// forces mode=chat_refine, strips the marker before passing the cleaned user
// text along to the LLM. TaskChatContainer filters the marker out of the
// rendered chat history so the user sees only the human-readable portion.
export const CHAT_REFINE_MARKER = '__FLIGHTLINE_CHAT_REFINE__';

const PATH_DB_SERVICE_FILES = '/Service_Files';
const PATH_DB_KNOWLEDGE_FILES = '/Knowledge_Files';
const PATH_DB_JOBS = '/jobs';

const PATH_CIM_RESULTS = '/cim_results';
const PATH_REPORT_RUNS = '/report_runs/insight_report_with_cim';

const PATH_CIM_RESULTS_OVERRIDES = '/cim_results_overrides';
const PATH_SETTINGS = '/settings';
const PATH_SCORE_OVERRIDE = '/score_override';

//RAG
const PATH_RAG = '/Service_RAG';
const PATH_CHAT = '/Service_Chat';

//Flow Tasks
const PATH_FLOWTASK_JOBS = '/flowtasks/jobs';
const PATH_FLOWTASK_LOG = '/flowtasks/log';
const PATH_FLOWTASK_TASK_STATE = '/flowtasks/task_state';
const PATH_FLOWTASK_HISTORY = '/flowtasks/history';
const PATH_FLOWTASK_VARS_EXIST_TABLE = '/flowtasks/vars_exist_table';
const PATH_FLOWTASK_GLOBAL_EVALS = '/flowtasks/evals';
const PATH_FLOWTASK_TASKS_DEFINITIONS = '/flowtasks/defs';
const PATH_FLOWISE_VARS = '/flowise_vars';
const PATH_VARS_STAGING = '/flowtasks/vars_staging';

export type FlowTaskStateType =
    | 'not_set'
    | 'queued'
    | 'in_progress'
    | 'completed'
    | 'failed';

const PATH_RAG_REMOTE_ACTION = 'Service_RAG_RemoteAction';
const PATH_ACTIVE_JOB = '/activeJob';
const PATH_USERS = '/users';
const PATH_FIRM_INFO = '/firm_info';
const PATH_REPORT_DEFINITIONS = '/report_definition';

export function sanitizeName(name) {
    const validChars = new Set(
        '-_()ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    );
    return [...name].filter(c => validChars.has(c)).join('');
}

/**
 * Resolve a content fingerprint for one attached file path. Prefers ragDocId
 * (content hash) from Service_Files; falls back to the raw filename when the
 * RAG pipeline hasn't processed this file yet. The result is what the
 * comparator detector stores and compares — never the full storage path.
 */
export function resolveAttachedFileFingerprint(
    reportId: string,
    attachedFilePath: string,
    allProjectFiles: {
        [reportId: string]: { files?: { [k: string]: { ragDocId?: string } } };
    }
): string {
    const rawName = attachedFilePath.split('/').pop() || attachedFilePath;
    const noExt = rawName.replace(/\.[^/.]+$/, '');
    const entryKey = sanitizeName(noExt).replace('_unlocked', '');
    const entry = allProjectFiles?.[reportId]?.files?.[entryKey];
    return entry?.ragDocId || rawName;
}

function dbBuildProjectFilePath(reportId: string, fileName: string) {
    const noExt = fileName.replace(/\.[^/.]+$/, '');
    const entryName = sanitizeName(noExt).replace('_unlocked', '');
    return `${PATH_DB_SERVICE_FILES}/${reportId}/files/${entryName}`;
}

export function dbKnowledgeFileSanitizer(fileStoragePath: string) {
    const noExt = fileStoragePath.replace(/\.[^/.]+$/, '');
    var path = noExt.replace('knowledge_', ''); //Only at start of string
    return sanitizeName(path.replaceAll('/', '__'));
}
export function dbBuildKnowledgeFilePath(fileStoragePath: string) {
    const entryName = dbKnowledgeFileSanitizer(fileStoragePath);
    return `${PATH_DB_KNOWLEDGE_FILES}/${entryName}`;
}

class FirebaseAdapater {
    app: FirebaseApp;
    db: Database;
    userId: string;
    user: User = null;
    private authInitialized: boolean = false;
    private navigationCallback: ((path: string) => void) | null = null;

    getConfig() {
        return useConfig().firebase;
    }

    setNavigationCallback(callback: (path: string) => void) {
        this.navigationCallback = callback;
    }

    private navigateToPath(path: string) {
        if (this.navigationCallback) {
            this.navigationCallback(path);
        } else {
            // Fallback to location.href if no navigation callback is set
            location.href = path;
        }
    }

    init() {
        const firebaseConfig = this.getConfig();
        console.log('Initializing Firebase');
        console.log(firebaseConfig);
        console.log('Is Developer: ', useStoreUI().isDev);

        this.app = initializeApp(firebaseConfig);
        this.db = getDatabase(this.app);
        var auth = getAuth(this.app);

        onAuthStateChanged(auth, user => {
            this.authInitialized = true;
            if (user) {
                console.log('Logged in!');
                this.userId = user.uid;
                this.user = user;

                const store = useStore();
                store.userId = user.uid;

                Sentry.setUser({
                    id: user.uid,
                    username: user.email,
                    email: user.email
                });

                //TODO: Clean this up a bit
                this.store_audit_entry('APP_LOAD');

                var r = ref(this.db, PATH_CIM_RESULTS);
                onValue(r, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.resetDataAccessCache();
                    store.reportDataDB = val || {};
                });

                var r_flowtask_states = ref(this.db, PATH_FLOWTASK_TASK_STATE);
                onValue(r_flowtask_states, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.flowTaskState = val || {};
                });

                var r_flowtask_vars_exist_table = ref(
                    this.db,
                    PATH_FLOWTASK_VARS_EXIST_TABLE
                );
                onValue(r_flowtask_vars_exist_table, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.flowTaskVarsExistTable = val || {};
                });

                var r_flowise_vars = ref(this.db, PATH_FLOWISE_VARS);
                onValue(r_flowise_vars, data => {
                    const val = data.val();
                    const store = useStore();
                    store.flowiseVarsDB = val || {};
                });

                var r_vars_staging = ref(this.db, PATH_VARS_STAGING);
                onValue(r_vars_staging, data => {
                    const val = data.val();
                    const store = useStore();
                    store.varsStagingDB = val || {};
                });

                var r_knowledgeFiles = ref(this.db, PATH_DB_KNOWLEDGE_FILES);
                onValue(r_knowledgeFiles, data => {
                    const val = data.val();
                    const store = useStore();
                    for (var fileEntryKey in val) {
                        val[fileEntryKey].saveKey = fileEntryKey;
                    }

                    store.knowledgeFilesDB = val || {};
                    store.areKnowledgeFilesLoaded = true;
                });

                var r_task_definitions = ref(
                    this.db,
                    PATH_FLOWTASK_TASKS_DEFINITIONS
                );
                onValue(r_task_definitions, data => {
                    const val = data.val();
                    const taskStore = useTasksStore();
                    const store = useStore();
                    for (var fileEntryKey in val) {
                        val[fileEntryKey].saveKey = fileEntryKey;
                    }

                    taskStore.tasksDefinitions = val || {};
                    store.areTaskDefinitionsLoaded = true;
                });

                var r_global_eval_rules = ref(
                    this.db,
                    PATH_FLOWTASK_GLOBAL_EVALS
                );
                onValue(r_global_eval_rules, data => {
                    const val = data.val();
                    const taskStore = useTasksStore();

                    taskStore.global_eval_rules = val || {};
                });

                var r_overrides = ref(this.db, PATH_CIM_RESULTS_OVERRIDES);
                onValue(r_overrides, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.resetDataAccessCache();

                    store.reportDataOverridesDB = val || {};
                    store.areOveridesLoaded = true;
                });

                var r_firminfo = ref(this.db, PATH_FIRM_INFO);
                onValue(r_firminfo, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.firmInfo = val || {};
                });

                var r_report_defs = ref(this.db, PATH_REPORT_DEFINITIONS);
                onValue(r_report_defs, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.reportDefinitions = val || {};
                });

                var r_project_files = ref(this.db, PATH_DB_SERVICE_FILES);
                onValue(r_project_files, data => {
                    const val = data.val();
                    const store = useStore();
                    for (var reportId in val) {
                        const files = val[reportId].files;
                        if (files) {
                            for (var key in files) {
                                files[key].saveKey = key;
                            }
                        }
                    }

                    store.allProjectFiles = val || {};
                });

                var r_rag = ref(this.db, PATH_RAG);
                onValue(r_rag, data => {
                    var val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    // for (var key in val) {
                    //     val[key]['originalDocId'] = key;
                    // }

                    store.ragDB = val || {};
                    store.isRagDbLoaded = true;
                });

                var r_settings = ref(this.db, PATH_SETTINGS);
                onValue(r_settings, data => {
                    const val = data.val();
                    const clientStore = useClientSettingStore();
                    const store = useStore();

                    store.areSettingsLoaded = true;
                    clientStore.settings = val || {};
                });

                var r_scoreoverride = ref(this.db, PATH_SCORE_OVERRIDE);
                onValue(r_scoreoverride, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const ruleStore = useScoreStore();
                    ruleStore.update_rules(val || {});

                    const store = useStore();
                    store.areScoreRulesLoaded = true;
                });

                var r = ref(this.db, PATH_DB_JOBS);
                onValue(r, data => {
                    const store = useStore();
                    store.jobs = data.val();
                });

                var r_task_log = query(
                    ref(this.db, PATH_FLOWTASK_LOG),
                    orderByKey(),
                    limitToLast(10)
                );

                onValue(r_task_log, data => {
                    const store = useStore();
                    store.tasksLog = data.val();
                });

                var chatsRef = ref(this.db, PATH_CHAT);
                onValue(chatsRef, data => {
                    const store = useStore();

                    store.chatData = data.val() ?? {};
                    store.isChatLoaded = true;
                });

                var activeJobs = ref(this.db, PATH_ACTIVE_JOB);
                onValue(activeJobs, data => {
                    const store = useStore();
                    store.activeJob = data.val();
                });

                var users = ref(this.db, PATH_USERS);
                onValue(users, data => {
                    const store = useStore();
                    store.users = data.val();
                });

                // Setup user comparables listener
                const userComparablesRef = ref(
                    this.db,
                    `user_compare/${this.userId}`
                );
                onValue(userComparablesRef, data => {
                    const store = useStore();
                    store.allUserComparables = data.val() || {};
                });

                // var onlineStatus = ref(this.db, '/system_status');
                // onValue(onlineStatus, data => {
                //     store.systemStatus = data.val();
                // });
            } else {
                if (location.href.indexOf('login') < 0) {
                    // Add small retry mechanism for auth state changes after deploys
                    let retryCount = 0;
                    const maxRetries = 3;
                    const retryDelay = 1000; // 1 second

                    const attemptRedirect = () => {
                        retryCount++;
                        if (this.authInitialized || retryCount >= maxRetries) {
                            this.navigateToPath('/login');
                        } else {
                            setTimeout(attemptRedirect, retryDelay);
                        }
                    };

                    attemptRedirect();
                }
            }
        });
    }

    deleteReport(reportId: string) {
        this.store_audit_entry('DELETE_DEAL', {
            reportId
        });

        var r = ref(this.db, `${PATH_CIM_RESULTS}/${reportId}`);
        set(r, null);
    }

    store_override(reportId: string, overidePath: string, value: any) {
        const path = `${PATH_CIM_RESULTS_OVERRIDES}/${reportId}/${overidePath}`;
        const switchDot = path.replace('.', '___');
        const r = ref(this.db, switchDot);
        set(r, value);

        const request_redo_summary = `${PATH_DB_SERVICE_FILES}/${reportId}/should_redo_summary`;
        const r2 = ref(this.db, request_redo_summary);
        set(r2, true);

        this.triggerScanRagFiles();

        this.store_audit_entry('EDIT_FIELD', {
            reportId: reportId,
            field: overidePath,
            value: value
        });

        // Update reference data snapshot if this deal has reference data
        this.update_reference_snapshot_if_exists(reportId);
    }

    remove_override(reportId: string, overidePath: string) {
        const path = `${PATH_CIM_RESULTS_OVERRIDES}/${reportId}/${overidePath}`;
        const switchDot = path.replace('.', '___');
        const r = ref(this.db, switchDot);
        set(r, null);

        this.store_audit_entry('EDIT_FIELD', {
            reportId: reportId,
            field: overidePath,
            value: 'DELETED'
        });

        // Update reference data snapshot if this deal has reference data
        this.update_reference_snapshot_if_exists(reportId);
    }

    async update_reference_snapshot_if_exists(dealId: string) {
        try {
            // Check if this deal has reference data
            const referenceRef = ref(
                this.db,
                `cim_results_reference/${dealId}`
            );
            const referenceSnapshot = await get(referenceRef);

            if (!referenceSnapshot.exists()) {
                // No reference data for this deal, nothing to update
                return;
            }

            const referenceData = referenceSnapshot.val();
            if (!referenceData.data || typeof referenceData.data !== 'object') {
                console.warn('Reference data exists but has unexpected format');
                return;
            }

            console.log(
                `Updating reference snapshot for deal ${dealId} due to override change`
            );

            // Get the current store
            const store = useStore();

            // Get current deal data with updated overrides
            const currentDealData = store.reportDataDB[dealId];
            const overrides = store.reportDataOverridesDB[dealId] || {};

            if (!currentDealData) {
                console.warn(`No deal data found for dealId: ${dealId}`);
                return;
            }

            // Create DataAccessUtils to get the current values with overrides
            const dataAccess = new DataAccessUtils(
                currentDealData,
                overrides,
                dealId
            );

            // Update only the fields that are in the reference data
            const updatedSnapshot = {};
            const referenceFields = Object.keys(referenceData.data);

            referenceFields.forEach(fieldName => {
                try {
                    // Get the current value with overrides applied
                    const fieldValue = dataAccess.value(fieldName);
                    updatedSnapshot[fieldName] = fieldValue;
                } catch (error) {
                    console.warn(
                        `Could not get updated value for field ${fieldName}:`,
                        error
                    );
                    // Keep the old value if we can't get the new one
                    updatedSnapshot[fieldName] = referenceData.data[fieldName];
                }
            });

            // Update the reference data with new snapshot
            const updatedReferenceData = {
                ...referenceData,
                data: updatedSnapshot,
                metadata: {
                    ...referenceData.metadata,
                    last_modified: serverTimestamp(),
                    auto_updated_due_to_override: true
                }
            };

            // Save the updated reference data
            await set(referenceRef, updatedReferenceData);

            console.log(
                `Successfully updated reference snapshot for deal ${dealId}`
            );
        } catch (error) {
            console.error(
                `Error updating reference snapshot for deal ${dealId}:`,
                error
            );
        }
    }

    store_audit_entry(type: AuditEntryType, extraData: any = {}) {
        if (!this.userId) {
            setTimeout(() => {
                this.store_audit_entry(type, extraData);
            }, 1000);
            return;
        }
        const userId = this.userId;
        const userEmail = this.user.email;

        const path = `audit_log`;
        const r = ref(this.db, path);

        push(r, {
            type: type,
            userId: userId,
            userEmail: userEmail,
            extraData: extraData,
            date: serverTimestamp()
        });
    }

    addDummyProjectFile(reportId: string, fileName: string) {
        const path = dbBuildProjectFilePath(reportId, fileName);
        const r = ref(this.db, path);
        update(r, {
            filename: fileName
        });
    }

    deleteKnowledgeFileDbEntry(fileStoragePath: string) {
        const path = dbBuildKnowledgeFilePath(fileStoragePath);
        const r = ref(this.db, path);
        set(r, null);
    }

    setKnowledgeFileTags(fileStoragePath: string, tags: IndustryTag[]) {
        const path = dbBuildKnowledgeFilePath(fileStoragePath);
        const r = ref(this.db, path);
        update(r, {
            tags: tags
        });
    }

    deleteProjectFile(reportId: string, filename: string) {
        const path = dbBuildProjectFilePath(reportId, filename);
        const r = ref(this.db, path);
        set(r, null);
    }

    triggerScanRagFiles() {
        const r = ref(this.db, PATH_RAG_REMOTE_ACTION);

        set(r, {
            action: 'import',
            started: false,
            timestamp: serverTimestamp()
        });
    }

    triggerKnowledgeFilesImport() {
        const r = ref(this.db, PATH_RAG_REMOTE_ACTION);

        set(r, {
            action: 'import_knowledge',
            started: false
        });
    }

    clearJobQueue() {
        const r = ref(this.db, '/jobs');
        set(r, null);
    }

    setJobStatus(jobId: string, status: string) {
        const r = ref(this.db, `${PATH_DB_JOBS}/${jobId}`);
        update(r, {
            status: status
        });
    }

    add_research_job(
        researchName: string,
        companyName: string,
        industry: string,
        website: string,
        reportId: string = '',
        debugSpecificSteps: number[] = null,
        runPostProcessing: boolean = true,
        createNewReport: boolean = true
    ) {
        const r = ref(this.db, PATH_DB_JOBS);
        const data = {
            fileName: researchName,
            user: getAuth().currentUser.email,
            userId: getAuth().currentUser.uid,
            priority: 0,
            status: 'pending',
            jobType: 'process_research',
            createdAt: serverTimestamp(),
            companyName: companyName,
            industry: industry,
            website: website,
            debug: {
                specificSteps: debugSpecificSteps,
                reportId: reportId,
                runPostProcessing: runPostProcessing,
                createNewReport: createNewReport
            }
        };

        push(r, data);
    }

    save_flowise_variable(reportId: string, varName: string, value: any) {
        const path = `flowise_vars/${reportId}/${varName}`;
        const r = ref(this.db, path);
        set(r, value);
    }

    async get_all_flowise_vars(
        reportId: string
    ): Promise<{ [key: string]: string }> {
        const path = `flowise_vars/${reportId}`;
        const r = ref(this.db, path);
        return (await get(r)).val() || {};
    }

    add_job(
        fileName: string,
        password?: string,
        originalJobId?: string,
        clearCache?: boolean,
        priority?: number
    ) {
        const r = ref(this.db, PATH_DB_JOBS);
        const data = {
            fileName: fileName,
            user: getAuth().currentUser.email,
            userId: getAuth().currentUser.uid,
            priority: priority || 0,
            status: 'pending',
            jobType: 'process_cim',
            createdAt: serverTimestamp()
        };

        if (password) {
            data['pdfPassword'] = password;
        }

        if (originalJobId) {
            data['originalJobId'] = originalJobId;
        }

        if (clearCache) {
            data['clearCache'] = true;
        }

        push(r, data);
    }

    chatbot_add_message(reportId: string, message: string, isMe: boolean) {
        const r = ref(this.db, `${PATH_CHAT}/${reportId}`);
        push(r, {
            message,
            isMe,
            processed: false,
            date: serverTimestamp()
        });
    }

    flowtask_state_set_queued(
        reportId: string,
        taskId: string,
        last_message_for_ui: FlowTaskMessage
    ) {
        const path = `${PATH_FLOWTASK_TASK_STATE}/${reportId}/${taskId}`;
        const r = ref(this.db, path);
        update(r, {
            last_message_for_ui,
            status: 'queued' as FlowTaskStateType,
            updatedAt: serverTimestamp(),
            updatedBy: 'frontend'
        });
    }

    flowtask_add_prompt_job(
        reportId: string,
        taskId: string,
        message: FlowTaskMessage
    ) {
        if (message.content == null || message.content.trim() == '') {
            message.content = '-';
        }
        const r = ref(this.db, `${PATH_FLOWTASK_JOBS}/`);
        push(r, {
            reportId,
            taskId,
            message,
            status: 'queued' as FlowTaskStateType,
            type: 'prompt',
            createdAt: serverTimestamp()
        });

        this.flowtask_state_set_queued(reportId, taskId, message);
    }
    flowtask_add_rerun_evals_job(reportId: string, taskId: string) {
        const r = ref(this.db, `${PATH_FLOWTASK_JOBS}/`);
        push(r, {
            reportId,
            taskId,
            status: 'queued' as FlowTaskStateType,
            type: 'rerun_evals',
            createdAt: serverTimestamp()
        });

        this.flowtask_state_set_queued(reportId, taskId, {
            content: 'Re-running evaluations...'
        });
    }

    /**
     * Sends a refinement message for the staging-proposal bubble's Discuss
     * flow. Prepends an invisible marker that the Master JS uses to force
     * mode=chat_refine and to recognize the message as a refinement turn.
     * The user's typed text follows the marker; Master strips it before
     * exposing the cleaned text to the LLM.
     */
    flowtask_invoke_chat_refine(
        reportId: string,
        taskId: string,
        userMessage: string
    ) {
        const message: FlowTaskMessage = {
            content: `${CHAT_REFINE_MARKER}${userMessage}`
        };
        this.flowtask_add_prompt_job(reportId, taskId, message);
    }

    /**
     * Append an agent (apiMessage) bubble to a module's chat history. Mirrors
     * the entries the flow writes for comparator/chat_refine turns so a
     * frontend-authored message renders identically to a flow-authored one.
     * History lives as an ordered array at
     * /flowtasks/history/{reportId}/{taskId}/results — read, push, write back.
     */
    private async flowtask_append_api_message(
        reportId: string,
        taskId: string,
        content: string
    ) {
        const resultsPath = `${PATH_FLOWTASK_HISTORY}/${reportId}/${taskId}/results`;
        const snap = await get(ref(this.db, resultsPath));
        const results: any[] = Array.isArray(snap.val()) ? snap.val() : [];
        results.push({
            role: 'apiMessage',
            content,
            id: `frontend_${Date.now()}`,
            createdDate: new Date().toISOString()
        });
        await set(ref(this.db, resultsPath), results);
    }

    async vars_staging_accept(
        reportId: string,
        taskId: string,
        varName: string,
        confirmationContent?: string
    ) {
        const stagingPath = `${PATH_VARS_STAGING}/${reportId}/${varName}`;
        const metaPath = `${PATH_VARS_STAGING}/${reportId}/${taskId}_metadata`;
        const canonicalPath = `${PATH_FLOWTASK_TASK_STATE}/../vars/${reportId}/${varName}`;
        // Canonical vars live under /flowtasks/vars/{reportId}/{varName}
        const canonicalRefPath = `/flowtasks/vars/${reportId}/${varName}`;

        const stagingSnap = await get(ref(this.db, stagingPath));
        const candidate = stagingSnap.val();
        if (candidate == null) return;

        const metaSnap = await get(ref(this.db, metaPath));
        const meta = metaSnap.val();

        // Copy candidate to canonical
        await set(ref(this.db, canonicalRefPath), candidate);

        // Grow the cumulative snapshot by union with this run's files. The
        // snapshot is the set of every file ever attached in this chat — it
        // never shrinks except via clear-chat. meta.files_snapshot holds the
        // files the comparator/chat_refine run consumed; we merge them into
        // the existing snapshot so re-attaching a previously-used file is a
        // no-op on the next run.
        if (meta && Array.isArray(meta.files_snapshot)) {
            const snapshotPath =
                `${PATH_FLOWTASK_TASK_STATE}/${reportId}/${taskId}/last_run_files_snapshot`;
            const prevSnap = await get(ref(this.db, snapshotPath));
            const previous: string[] = Array.isArray(prevSnap.val())
                ? prevSnap.val()
                : [];
            const merged = previous.slice();
            for (const f of meta.files_snapshot as string[]) {
                if (!merged.includes(f)) merged.push(f);
            }
            await set(ref(this.db, snapshotPath), merged);
        }

        // Bump task_state.updatedAt so staleness recalculates
        const taskStateRoot = `${PATH_FLOWTASK_TASK_STATE}/${reportId}/${taskId}`;
        await update(ref(this.db, taskStateRoot), {
            updatedAt: serverTimestamp(),
            updatedBy: 'frontend'
        });

        // Echo the acceptance into chat history so the thread doesn't go silent
        // when the staging bubble disappears. Without this the user sees the
        // proposal vanish with no trace that anything was applied.
        if (confirmationContent) {
            await this.flowtask_append_api_message(
                reportId,
                taskId,
                confirmationContent
            );
        }

        // Remove staging
        await set(ref(this.db, stagingPath), null);
        await set(ref(this.db, metaPath), null);
    }

    async vars_staging_reject(reportId: string, taskId: string, varName: string) {
        const stagingPath = `${PATH_VARS_STAGING}/${reportId}/${varName}`;
        const metaPath = `${PATH_VARS_STAGING}/${reportId}/${taskId}_metadata`;
        await set(ref(this.db, stagingPath), null);
        await set(ref(this.db, metaPath), null);
    }

    /**
     * Clean up the v2 paths the backend's "clear" job doesn't know about:
     * the staging proposal, its metadata, and the file snapshot used by the
     * comparator detector. The backend already removes the canonical
     * `vars/{reportId}/{varName}` via `tasks_db.remove_variable` — this
     * complements that so a "Restart chat & Delete conversation" leaves no
     * residue in module-related state.
     */
    async flowtask_clear_v2_state(
        reportId: string,
        taskId: string,
        varName: string
    ) {
        const stagingPath = `${PATH_VARS_STAGING}/${reportId}/${varName}`;
        const metaPath = `${PATH_VARS_STAGING}/${reportId}/${taskId}_metadata`;
        const snapshotPath =
            `${PATH_FLOWTASK_TASK_STATE}/${reportId}/${taskId}/last_run_files_snapshot`;
        await Promise.all([
            set(ref(this.db, stagingPath), null),
            set(ref(this.db, metaPath), null),
            set(ref(this.db, snapshotPath), null)
        ]);
    }

    flowtask_clear_all_variables(reportId: string) {
        const r = ref(this.db, `${PATH_FLOWTASK_JOBS}/`);
        push(r, {
            reportId,
            status: 'queued' as FlowTaskStateType,
            type: 'remove_all_variables',
            createdAt: serverTimestamp()
        });
    }
    flowtask_add_clear_job(reportId: string, taskId: string) {
        const r = ref(this.db, `${PATH_FLOWTASK_JOBS}/`);
        push(r, {
            reportId,
            taskId,
            status: 'queued' as FlowTaskStateType,
            type: 'clear',
            createdAt: serverTimestamp()
        });

        this.flowtask_state_set_queued(reportId, taskId, {
            content: 'Clearing task state...'
        });
    }
    clear_chat(reportId: string) {
        const r = ref(this.db, `${PATH_CHAT}/${reportId}`);
        set(r, null);
        const r2 = ref(this.db, `R2R_Company_To_Conversation_Id/${reportId}`);
        set(r2, null);
    }

    save_reference_data(
        dealId: string,
        selectedFields: { [fieldName: string]: boolean }
    ) {
        // Get fields that should be saved as reference
        const fieldsToSave = Object.keys(selectedFields).filter(
            field => selectedFields[field]
        );

        if (fieldsToSave.length === 0) {
            console.log('No fields selected to save');
            return {
                success: false,
                message: 'No fields selected to save'
            };
        }

        try {
            console.log(
                `Saving ${fieldsToSave.length} fields as reference data for deal: ${dealId}`
            );

            // Get the current store
            const store = useStore();

            // Get current deal data with overrides
            const currentDealData = store.reportDataDB[dealId];
            const overrides = store.reportDataOverridesDB[dealId] || {};

            if (!currentDealData) {
                console.error('No deal data found for dealId:', dealId);
                return {
                    success: false,
                    message: 'No deal data found for this deal'
                };
            }

            // Create DataAccessUtils to get the current values
            const dataAccess = new DataAccessUtils(
                currentDealData,
                overrides,
                dealId
            );

            // Create snapshot of actual data values for selected fields
            const dataSnapshot = {};
            fieldsToSave.forEach(fieldName => {
                try {
                    // Get the current value using DataAccessUtils
                    const fieldValue = dataAccess.value(fieldName);
                    dataSnapshot[fieldName] = fieldValue;
                } catch (error) {
                    console.warn(
                        `Could not get value for field ${fieldName}:`,
                        error
                    );
                    dataSnapshot[fieldName] = null;
                }
            });

            // Save snapshot structure with actual data values
            const referenceData = {
                data: dataSnapshot,
                metadata: {
                    saved_at: serverTimestamp(),
                    saved_by: this.userId,
                    saved_by_email: this.user.email,
                    backend_version: 'unknown',
                    last_modified: serverTimestamp(),
                    field_count: fieldsToSave.length
                }
            };

            // Save to Firebase
            const path = `cim_results_reference/${dealId}`;
            const r = ref(this.db, path);
            set(r, referenceData);

            // Log audit entry
            this.store_audit_entry('SAVE_REFERENCE_DATA', {
                dealId: dealId,
                fieldCount: fieldsToSave.length,
                fields: fieldsToSave
            });

            // Return success message for UI feedback
            return {
                success: true,
                message: `Saved ${fieldsToSave.length} fields as reference data for testing.`
            };
        } catch (error) {
            console.error('Error saving reference data:', error);
            return {
                success: false,
                message: `Error saving reference data: ${error.message}`
            };
        }
    }

    async has_reference_data(dealId: string): Promise<boolean> {
        try {
            const referenceRef = ref(
                this.db,
                `cim_results_reference/${dealId}`
            );
            const snapshot = await get(referenceRef);
            return snapshot.exists() && snapshot.val()?.data;
        } catch (error) {
            console.error(
                `Error checking reference data for deal ${dealId}:`,
                error
            );
            return false;
        }
    }

    get_existing_reference_data(dealId: string) {
        return new Promise((resolve, reject) => {
            if (!dealId) {
                console.log(
                    'No dealId provided to get_existing_reference_data'
                );
                resolve(null);
                return;
            }

            console.log(`Getting reference data for deal: ${dealId}`);
            const r = ref(this.db, `cim_results_reference/${dealId}`);

            get(r)
                .then(snapshot => {
                    if (snapshot.exists()) {
                        console.log('Found existing reference data');
                        const data = snapshot.val();

                        // Check if we have the data snapshot structure
                        if (data.data && typeof data.data === 'object') {
                            // Transform the data object into field selections for the UI
                            const fieldSelections = {};
                            Object.keys(data.data).forEach(fieldName => {
                                fieldSelections[fieldName] = true;
                            });

                            // Return the properly formatted object for SelectionStore
                            resolve({
                                data: fieldSelections,
                                metadata: data.metadata || {}
                            });
                        } else {
                            console.warn(
                                'Reference data exists but has unexpected format:',
                                data
                            );
                            resolve({
                                data: {},
                                metadata: data.metadata || {}
                            });
                        }
                    } else {
                        console.log('No existing reference data found');
                        resolve(null);
                    }
                })
                .catch(error => {
                    console.error('Error getting reference data:', error);
                    reject(error);
                });
        });
    }

    async saveUserComparables(
        userId: string,
        dealId: string,
        comparables: (string | null)[]
    ) {
        if (!userId || !dealId) {
            console.warn('Cannot save comparables: missing dealId or userId');
            return false;
        }

        try {
            const comparablesRef = ref(
                this.db,
                `user_compare/${userId}/${dealId}`
            );

            await set(comparablesRef, {
                comparables,
                lastUpdated: serverTimestamp()
            });

            return true;
        } catch (error) {
            console.error('Error saving user comparables:', error);
            return false;
        }
    }
}

const mFirebaseService = new FirebaseAdapater();
export function getFirebase() {
    return mFirebaseService;
}
